import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { APP } from './tokens';

type Tab = 'draw' | 'type' | 'upload';

export type SignatureCaptureHandle = {
  /** Export current tab to PNG data URL if there is something to commit; otherwise null */
  tryCommit: () => string | null;
};

interface SignatureCaptureProps {
  onCapture: (dataUrl: string) => void;
  onCancel?: () => void;
  /** Label shown above the component, e.g. "Client Signature" */
  fieldLabel?: string;
  /** Fires when the active tab has ink/text/upload worth committing (without auto-calling onCapture) */
  onStageReadyChange?: (ready: boolean) => void;
}

const SignatureCapture = forwardRef<SignatureCaptureHandle, SignatureCaptureProps>(function SignatureCapture(
  { onCapture, onCancel, fieldLabel, onStageReadyChange },
  ref,
) {
  const [activeTab, setActiveTab] = useState<Tab>('draw');
  const [typedName, setTypedName] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);

  const drawCommitRef = useRef<() => string | null>(() => null);
  const typeCommitRef = useRef<() => string | null>(() => null);
  const uploadCommitRef = useRef<() => string | null>(() => null);

  useImperativeHandle(
    ref,
    () => ({
      tryCommit: () => {
        if (activeTab === 'draw') return drawCommitRef.current();
        if (activeTab === 'type') return typeCommitRef.current();
        return uploadCommitRef.current();
      },
    }),
    [activeTab],
  );

  useEffect(() => {
    if (activeTab !== 'upload') setUploadPreview(null);
  }, [activeTab]);

  useEffect(() => {
    let ready = false;
    if (activeTab === 'draw') ready = hasDrawn;
    else if (activeTab === 'type') ready = !!typedName.trim();
    else ready = !!uploadPreview;
    onStageReadyChange?.(ready);
  }, [activeTab, hasDrawn, typedName, uploadPreview, onStageReadyChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {fieldLabel && (
        <p style={{ margin: 0, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: APP.textSecondary }}>
          {fieldLabel}
        </p>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', borderRadius: 6, backgroundColor: '#F5F0EB', padding: 3 }}>
        {(['draw', 'type', 'upload'] as Tab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1, border: 'none', cursor: 'pointer', padding: '7px 0', fontSize: 12,
              fontFamily: APP.fontFamily, fontWeight: activeTab === tab ? 500 : 400,
              borderRadius: 4, color: activeTab === tab ? APP.textPrimary : APP.textMuted,
              backgroundColor: activeTab === tab ? APP.bgCardBody : 'transparent',
              boxShadow: activeTab === tab ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
              transition: 'all 0.15s ease',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'draw' && (
        <DrawTab
          onCapture={onCapture}
          hasDrawn={hasDrawn}
          setHasDrawn={setHasDrawn}
          bindCommit={(fn) => {
            drawCommitRef.current = fn;
          }}
        />
      )}
      {activeTab === 'type' && (
        <TypeTab
          value={typedName}
          onChange={setTypedName}
          onCapture={onCapture}
          bindCommit={(fn) => {
            typeCommitRef.current = fn;
          }}
        />
      )}
      {activeTab === 'upload' && (
        <UploadTab
          onCapture={onCapture}
          preview={uploadPreview}
          setPreview={setUploadPreview}
          bindCommit={(fn) => {
            uploadCommitRef.current = fn;
          }}
        />
      )}

      {/* Cancel */}
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          style={{
            border: '1px solid #E8DED4', borderRadius: 6, padding: '8px 12px',
            backgroundColor: 'transparent', color: APP.textCardDetail, fontSize: 12,
            cursor: 'pointer', fontFamily: APP.fontFamily,
          }}
        >
          Cancel
        </button>
      )}
    </div>
  );
});

export default SignatureCapture;

// ─── Draw Tab ───────────────────────────────────────────────────────

function DrawTab({
  onCapture,
  hasDrawn,
  setHasDrawn,
  bindCommit,
}: {
  onCapture: (dataUrl: string) => void;
  hasDrawn: boolean;
  setHasDrawn: (v: boolean) => void;
  bindCommit: (fn: () => string | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  // Scale canvas to match container width for proper coordinate mapping
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const w = container.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = 120 * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);
  }, []);

  const getPos = useCallback((clientX: number, clientY: number) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const startDraw = useCallback((x: number, y: number) => {
    isDrawingRef.current = true;
    lastPosRef.current = { x, y };
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    // Single tap / touch-without-move never hit drawLine, so hasDrawn stayed false and
    // downstream CTAs (e.g. recipient “Apply signature to document”) never lit up.
    ctx.fillStyle = '#2B2420';
    ctx.beginPath();
    ctx.arc(x, y, 1.25, 0, Math.PI * 2);
    ctx.fill();
    setHasDrawn(true);
  }, [setHasDrawn]);

  const drawLine = useCallback((x: number, y: number) => {
    if (!isDrawingRef.current || !lastPosRef.current) return;
    const ctx = canvasRef.current!.getContext('2d')!;
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#2B2420';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPosRef.current = { x, y };
    setHasDrawn(true);
  }, [setHasDrawn]);

  const endDraw = useCallback(() => {
    isDrawingRef.current = false;
    lastPosRef.current = null;
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pos = getPos(e.clientX, e.clientY);
    startDraw(pos.x, pos.y);
  }, [getPos, startDraw]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e.clientX, e.clientY);
    drawLine(pos.x, pos.y);
  }, [getPos, drawLine]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    endDraw();
  }, [endDraw]);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
    setHasDrawn(false);
  }, [setHasDrawn]);

  const applySignature = useCallback(() => {
    if (!canvasRef.current) return;
    onCapture(canvasRef.current.toDataURL('image/png'));
  }, [onCapture]);

  useEffect(() => {
    bindCommit(() => {
      if (!hasDrawn || !canvasRef.current) return null;
      return canvasRef.current.toDataURL('image/png');
    });
    return () => bindCommit(() => null);
  }, [hasDrawn, bindCommit]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div ref={containerRef} style={{ position: 'relative', border: '1px solid #E8DED4', borderRadius: 8, overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onPointerLeave={endDraw}
          style={{ display: 'block', width: '100%', height: 120, cursor: 'crosshair', backgroundColor: '#FFFFFF', touchAction: 'none' }}
        />
        {!hasDrawn && (
          <p style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            margin: 0, fontSize: 13, fontFamily: 'Georgia, serif', fontStyle: 'italic', color: '#C4A99A',
            pointerEvents: 'none',
          }}>
            Draw your signature here
          </p>
        )}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={clearCanvas}
          style={{
            flex: 1, border: '1px solid #E8DED4', borderRadius: 6, padding: '8px 12px',
            backgroundColor: 'transparent', color: APP.textCardDetail, fontSize: 12, cursor: 'pointer',
            fontFamily: APP.fontFamily,
          }}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={applySignature}
          disabled={!hasDrawn}
          style={{
            flex: 2, border: 'none', borderRadius: 6, padding: '8px 12px',
            backgroundColor: hasDrawn ? APP.textDark : '#E8DED4', color: hasDrawn ? '#FDFCFB' : APP.textMuted,
            fontSize: 12, fontWeight: 500, cursor: hasDrawn ? 'pointer' : 'default',
            fontFamily: APP.fontFamily,
          }}
        >
          Apply Signature
        </button>
      </div>
    </div>
  );
}

// ─── Type Tab ───────────────────────────────────────────────────────

function TypeTab({
  value,
  onChange,
  onCapture,
  bindCommit,
}: {
  value: string;
  onChange: (v: string) => void;
  onCapture: (dataUrl: string) => void;
  bindCommit: (fn: () => string | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const renderTypedToDataUrl = useCallback((): string | null => {
    const canvas = canvasRef.current;
    if (!canvas || !value.trim()) return null;
    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = 'italic 32px Georgia, serif';
    ctx.fillStyle = '#2B2420';
    ctx.textBaseline = 'middle';
    ctx.fillText(value, 16, canvas.height / 2);
    return canvas.toDataURL('image/png');
  }, [value]);

  const applyTyped = useCallback(() => {
    const url = renderTypedToDataUrl();
    if (url) onCapture(url);
  }, [renderTypedToDataUrl, onCapture]);

  useEffect(() => {
    bindCommit(() => renderTypedToDataUrl());
    return () => bindCommit(() => null);
  }, [value, bindCommit, renderTypedToDataUrl]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type your name"
        style={{
          border: '1px solid #E8DED4', borderRadius: 8, padding: '10px 14px',
          fontSize: 14, fontFamily: APP.fontFamily, color: '#2B2420',
          backgroundColor: '#FAFAF8', outline: 'none',
        }}
      />
      {/* Preview */}
      <div style={{ border: '1px solid #E8DED4', borderRadius: 8, padding: 16, minHeight: 60, backgroundColor: '#FFFFFF', display: 'flex', alignItems: 'center' }}>
        <span style={{ fontSize: 28, fontFamily: 'Georgia, serif', fontStyle: 'italic', color: value ? '#2B2420' : '#C4A99A' }}>
          {value || 'Preview'}
        </span>
      </div>
      <canvas ref={canvasRef} width={300} height={80} style={{ display: 'none' }} />
      <button
        type="button"
        onClick={applyTyped}
        disabled={!value.trim()}
        style={{
          border: 'none', borderRadius: 6, padding: '8px 12px',
          backgroundColor: value.trim() ? APP.textDark : '#E8DED4', color: value.trim() ? '#FDFCFB' : APP.textMuted,
          fontSize: 12, fontWeight: 500, cursor: value.trim() ? 'pointer' : 'default',
          fontFamily: APP.fontFamily, width: '100%',
        }}
      >
        Apply Signature
      </button>
    </div>
  );
}

// ─── Upload Tab ─────────────────────────────────────────────────────

function UploadTab({
  onCapture,
  preview,
  setPreview,
  bindCommit,
}: {
  onCapture: (dataUrl: string) => void;
  preview: string | null;
  setPreview: (v: string | null) => void;
  bindCommit: (fn: () => string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setPreview(dataUrl);
    };
    reader.readAsDataURL(file);
  }, [setPreview]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  useEffect(() => {
    bindCommit(() => preview);
    return () => bindCommit(() => null);
  }, [preview, bindCommit]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div
        onClick={() => inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        style={{
          border: '2px dashed #E8DED4', borderRadius: 8, padding: 24,
          textAlign: 'center', cursor: 'pointer', backgroundColor: '#FAFAF8',
          minHeight: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        {preview ? (
          <img src={preview} alt="Signature" style={{ maxWidth: '100%', maxHeight: 80 }} />
        ) : (
          <>
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke={APP.textSecondary} strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
            </svg>
            <p style={{ margin: 0, fontSize: 12, color: APP.textSecondary }}>Drop image or click to upload</p>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
        />
      </div>
      <button
        type="button"
        onClick={() => preview && onCapture(preview)}
        disabled={!preview}
        style={{
          border: 'none', borderRadius: 6, padding: '8px 12px',
          backgroundColor: preview ? APP.textDark : '#E8DED4', color: preview ? '#FDFCFB' : APP.textMuted,
          fontSize: 12, fontWeight: 500, cursor: preview ? 'pointer' : 'default',
          fontFamily: APP.fontFamily, width: '100%',
        }}
      >
        Apply Signature
      </button>
    </div>
  );
}
